import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import CrossfadeImage from './CrossfadeImage';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../translations';
import { deduplicateRecurring } from '../utils/deduplicateRecurring';
import { upcomingQueryStart, isOngoingOrUpcoming, getPanelDateFields } from '../utils/eventDates';
import EventDateTime from './EventDateTime';
import './EventPanels.css';


function EventPanels() {
  const { language } = useLanguage();
  const [allEvents, setAllEvents] = useState([]);
  const [musicEvents, setMusicEvents] = useState([]);
  const [foodEvents, setFoodEvents] = useState([]);
  const [youtubeVideos, setYoutubeVideos] = useState([]);
  const [currentCenterIndex, setCurrentCenterIndex] = useState(0);


  // Default/fallback events
  const defaultMusicEvents = [
    {
      image: '/images/studio.png',
      title: t('eventPanels.recordingSessions', language),
      subtitle: t('eventPanels.recordingSubtitle', language)
    }
  ];

  const defaultFoodEvents = [
    {
      image: '/images/aerialview.png',
      title: t('eventPanels.outdoorDining', language),
      subtitle: t('eventPanels.outdoorSubtitle', language)
    }
  ];

  useEffect(() => {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    // Queries are widened so multi-day events already underway stay listed;
    // isOngoingOrUpcoming filters out genuinely past events client-side.
    // Limits are raised to compensate for the filtered-out extras.
    const allEventsQuery = query(
      collection(db, 'events'),
      where('date', '>=', upcomingQueryStart(startOfToday)),
      orderBy('date', 'asc'),
      limit(20)
    );

    // Query for upcoming music events
    const musicQuery = query(
      collection(db, 'events'),
      where('type', '==', 'music'),
      where('date', '>=', upcomingQueryStart(startOfToday)),
      orderBy('date', 'asc'),
      limit(12)
    );

    // Query for upcoming food events
    const foodQuery = query(
      collection(db, 'events'),
      where('type', '==', 'food'),
      where('date', '>=', upcomingQueryStart(startOfToday)),
      orderBy('date', 'asc'),
      limit(12)
    );

    // Query for YouTube videos (remove isActive filter for now to debug)
    const youtubeQuery = query(
      collection(db, 'youtubeVideos'),
      orderBy('createdAt', 'desc'),
      limit(5)
    );

    const unsubscribeAll = onSnapshot(allEventsQuery, (snapshot) => {
      const events = snapshot.docs
        .filter(doc => isOngoingOrUpcoming(doc.data(), startOfToday))
        .map(doc => {
        const data = doc.data();
        const eventDate = data.date && data.date.toDate ? data.date.toDate() : new Date(data.date);
        return {
          id: doc.id,
          image: (data.thumbnailUrl || data.imageUrl) || '/images/studio.png',
          title: (language === 'sv' && data.title_sv) || data.title,
          subtitle: (language === 'sv' && data.subtitle_sv) || data.subtitle || '',
          date: eventDate,
          type: data.type,
          price: data.price || 250,
          memberPrice: data.memberPrice || 200,
          ...getPanelDateFields(data.date, data.endDate, language === 'sv' ? 'sv-SE' : 'en-GB'),
          recurringGroupId: data.recurringGroupId || null
        };
      });

      setAllEvents(deduplicateRecurring(events).slice(0, 10));
    });

    const unsubscribeMusic = onSnapshot(musicQuery, (snapshot) => {
      const events = snapshot.docs
        .filter(doc => isOngoingOrUpcoming(doc.data(), startOfToday))
        .map(doc => {
        const data = doc.data();
        const eventDate = data.date && data.date.toDate ? data.date.toDate() : new Date(data.date);
        return {
          id: doc.id,
          image: (data.thumbnailUrl || data.imageUrl) || '/images/studio.png',
          title: (language === 'sv' && data.title_sv) || data.title,
          subtitle: (language === 'sv' && data.subtitle_sv) || data.subtitle || '',
          date: eventDate,
          ...getPanelDateFields(data.date, data.endDate, language === 'sv' ? 'sv-SE' : 'en-GB'),
          recurringGroupId: data.recurringGroupId || null
        };
      });

      const dedupedMusic = deduplicateRecurring(events);
      const combinedMusicEvents = dedupedMusic.length > 0
        ? [...dedupedMusic, ...defaultMusicEvents].slice(0, 5)
        : defaultMusicEvents;

      setMusicEvents(combinedMusicEvents);
    });

    const unsubscribeFood = onSnapshot(foodQuery, (snapshot) => {
      const events = snapshot.docs
        .filter(doc => isOngoingOrUpcoming(doc.data(), startOfToday))
        .map(doc => {
        const data = doc.data();
        const eventDate = data.date && data.date.toDate ? data.date.toDate() : new Date(data.date);
        return {
          id: doc.id,
          image: (data.thumbnailUrl || data.imageUrl) || '/images/restaurant.png',
          title: (language === 'sv' && data.title_sv) || data.title,
          subtitle: (language === 'sv' && data.subtitle_sv) || data.subtitle || '',
          date: eventDate,
          ...getPanelDateFields(data.date, data.endDate, language === 'sv' ? 'sv-SE' : 'en-GB'),
          recurringGroupId: data.recurringGroupId || null
        };
      });

      const dedupedFood = deduplicateRecurring(events);
      const combinedFoodEvents = dedupedFood.length > 0
        ? [...dedupedFood, ...defaultFoodEvents].slice(0, 5)
        : defaultFoodEvents;

      setFoodEvents(combinedFoodEvents);
    });

    const unsubscribeYoutube = onSnapshot(youtubeQuery, (snapshot) => {
      const videos = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          videoId: data.videoId,
          title: data.title || 'YOUTUBE',
          description: data.description || t('eventPanels.checkLatestVideo', language),
          thumbnail: data.thumbnail,
          url: data.url,
          createdAt: data.createdAt,
          type: 'youtube'
        };
      });
      
      setYoutubeVideos(videos);
    });

    return () => {
      unsubscribeAll();
      unsubscribeMusic();
      unsubscribeFood();
      unsubscribeYoutube();
    };
  }, [language]); // eslint-disable-line react-hooks/exhaustive-deps


  // Get the next upcoming event for the static left panel (first from allEvents or fallback to default)
  const nextEvent = allEvents.length > 0 ? allEvents[0] : defaultMusicEvents[0];
  
  // Get events for center panel cycling (exclude the UP NEXT event)
  const centerPanelEvents = allEvents.length > 1 ? allEvents.slice(1) : [];
  
  // Auto-advance carousel for center panel every 10 seconds
  useEffect(() => {
    if (centerPanelEvents.length > 1) {
      const interval = setInterval(() => {
        setCurrentCenterIndex(prev => (prev + 1) % centerPanelEvents.length);
      }, 10000); // Changed to 10 seconds

      return () => clearInterval(interval);
    }
  }, [centerPanelEvents.length]);

  // Get cycling event for center panel
  const centerEvent = centerPanelEvents.length > 0 
    ? centerPanelEvents[currentCenterIndex % centerPanelEvents.length] 
    : null;
  
  // Get YouTube video for right panel (always show the latest/first video, or fallback to default)
  const rightContent = youtubeVideos.length > 0 ? youtubeVideos[0] : defaultMusicEvents[1];
  
  return (
  <section className="event-panels" id="events">
    <div className="panels-container">
      {/* Left Panel - Static "UP NEXT" (no transition) */}
      <div className="event-panel static-panel">
        <div className="panel-header">
          <h2>{t('eventPanels.upNext', language)}</h2>
        </div>
        <div className="panel-content">
          {nextEvent && nextEvent.id ? (
            <>
              <Link to={`/event/${nextEvent.id}`} className="event-link">
                {/* Plain background-image block (no crossfade here) */}
                <div
                  className="event-image-container"
                  style={{
                    backgroundImage:
                      `url(${
                        (nextEvent && (nextEvent.thumbnailUrl || nextEvent.imageUrl || nextEvent.image)) ||
                        '/images/lunch.png'
                      })`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  }}
                />
                <div className="event-info">
                  <div className="event-datetime">
                    <EventDateTime event={nextEvent} />
                  </div>
                  <h3>{nextEvent.title}</h3>
                  <p>{nextEvent.subtitle}</p>
                </div>
              </Link>
            </>
          ) : (
            <>
              <div
                className="event-image-container"
                style={{
                  backgroundImage:
                    `url(${
                      (nextEvent && (nextEvent.imageUrl || nextEvent.image)) ||
                      '/images/lunch.png'
                    })`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                }}
              />
              <div className="event-info">
                {nextEvent && nextEvent.formattedDate && (
                  <div className="event-datetime">
                    <EventDateTime event={nextEvent} />
                  </div>
                )}
                <h3>{(nextEvent && nextEvent.title) || t('eventPanels.event', language)}</h3>
                <p>{(nextEvent && nextEvent.subtitle) || t('eventPanels.comingSoon', language)}</p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Center Panel - Cycling (WITH transition) */}
      <div className="event-panel cycling-panel">
        <div className="panel-header">
          <h2>{t('eventPanels.comingUp', language)}</h2>
        </div>
        <div className="panel-content">
          {centerEvent && centerEvent.id ? (
            <>
              <Link to={`/event/${centerEvent.id}`} className="event-link">
                {/* Crossfade ONLY here */}
                <CrossfadeImage
                  src={
                    (centerEvent && (centerEvent.thumbnailUrl || centerEvent.imageUrl || centerEvent.image)) ||
                    '/images/studio.png'
                  }
                  alt={(centerEvent && centerEvent.title) || 'Event image'}
                  duration={1800} /* slower, smoother dissolve */
                />
                <div className="event-info">
                  <div className="event-datetime">
                    <EventDateTime event={centerEvent} />
                  </div>
                  <h3>{centerEvent.title}</h3>
                  <p>{centerEvent.subtitle}</p>
                </div>
              </Link>
            </>
          ) : (
            <>
              <div
                className="event-image-container"
                style={{
                  backgroundImage:
                    `url(${
                      (centerEvent && (centerEvent.thumbnailUrl || centerEvent.imageUrl || centerEvent.image)) ||
                      '/images/studio.png'
                    })`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                }}
              />
              <div className="event-info">
                {centerEvent && centerEvent.formattedDate && (
                  <div className="event-datetime">
                    <EventDateTime event={centerEvent} />
                  </div>
                )}
                <h3>{(centerEvent && centerEvent.title) || t('eventPanels.event', language)}</h3>
                <p>{(centerEvent && centerEvent.subtitle) || t('eventPanels.comingSoon', language)}</p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Right Panel - YouTube or Events (no transition) */}
      <div className="event-panel cycling-panel youtube-panel">
        <div className="panel-header">
          <h2>{(rightContent && rightContent.type === 'youtube') ? 'YOUTUBE' : t('eventPanels.happening', language)}</h2>
        </div>
        <div className="panel-content">
          {rightContent && rightContent.type === 'youtube' ? (
            <a
              href={rightContent.url}
              target="_blank"
              rel="noopener noreferrer"
              className="youtube-link"
            >
              <div
                className="event-image-container"
                style={{
                  backgroundImage: `url(${rightContent.thumbnail})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                }}
              />
              <div className="event-info">
                <h3>{rightContent.title}</h3>
                <p>{rightContent.description}</p>
              </div>
            </a>
          ) : (
            <>
              <div
                className="event-image-container"
                style={{
                  backgroundImage:
                    `url(${
                      (rightContent && (rightContent.thumbnailUrl || rightContent.imageUrl || rightContent.image)) ||
                      '/images/pianobarn.png'
                    })`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                }}
              />
              <div className="event-info">
                {rightContent && rightContent.formattedDate && (
                  <div className="event-datetime">
                    <EventDateTime event={rightContent} />
                  </div>
                )}
                <h3>{(rightContent && rightContent.title) || t('eventPanels.event', language)}</h3>
                <p>{(rightContent && rightContent.subtitle) || t('eventPanels.comingSoon', language)}</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  </section>
);}

export default EventPanels;
